import type { IUpdateProfileCmd } from "@/data/commands/profile/UpdateProfileCmd";
import { Factory } from "@/data/dao/Factory";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import { ServiceType } from "@/data/model/Enums";
import type { IProfile } from "@/data/model/Profile";
import { Validation } from "@/data/util/Validation";

import { logError } from "@/shared/logging/errors";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { Ripple } from "@/shared/ui/Ripple";

import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { BaseController } from "@/app/controller/BaseController";
import { AvatarInput } from "@/app/elements/AvatarInput";
import { DateInput } from "@/app/elements/DateInput";
import { EmailInput } from "@/app/elements/EmailInput";
import { PasswordInput } from "@/app/elements/PasswordInput";
import { PhoneNumberInput } from "@/app/elements/PhoneNumberInput";
import { trans } from "@/app/localization/Trans";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";

export interface IProfileControllerTransitionData {
    phoneNumberRequired: boolean;
}

export class ProfileController extends BaseController {
    public onGoBack?: { () };

    private $profileForm: JQuery;
    private $saveBtn: JQuery;

    private avatarInput: AvatarInput;
    private emailInput: EmailInput;
    private currentPwInput: PasswordInput;
    private newPwInput: PasswordInput;
    private phoneInput: PhoneNumberInput;
    private birthdayInput: DateInput;

    private $changePwBtn: JQuery;
    private $resendConfirmBtn: JQuery;

    private useContactTracing?: boolean;
    private useCertificateVerification?: boolean;
    private isPhoneNumberRequired: boolean | undefined = undefined;

    constructor() {
        super("profile");

        const hdr = $('<div class="head"><div class="title">' + trans.profile.title + "</div></div>").appendTo(
            this.$view
        );
        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();
                if (this.onGoBack !== undefined) this.onGoBack();
            });

        const scrollable = $('<div class="scrollable" />').appendTo(this.$view);
        const top = $('<div class="top" />').appendTo(scrollable);
        this.avatarInput = new AvatarInput().appendTo(top);

        this.$profileForm = $('<form class="form" autocomplete="off" />').appendTo(scrollable);
        this.$profileForm.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.updateProfile();
        });

        let row = $('<div class="formrow splitted" />').appendTo(this.$profileForm);
        let col = $('<div class="col" />').appendTo(row);
        col.append(
            '<div class="formcontrol"><label>' +
                trans.signup.firstName +
                '</label><input type="text" name="firstname" autocomplete="off" value="" /></div>'
        );

        col = $('<div class="col" />').appendTo(row);
        col.append(
            '<div class="formcontrol"><label>' +
                trans.signup.lastName +
                '</label><input type="text" name="lastname" autocomplete="off" value="" /></div>'
        );

        row = $('<div class="formrow" />').appendTo(this.$profileForm);
        this.emailInput = new EmailInput(false).appendTo(row);

        this.$resendConfirmBtn = $('<a href="#" class="resendconfirm">' + trans.profile.resendConfirmation + "</a>")
            .appendTo(this.$profileForm)
            .click(async (e) => {
                e.preventDefault();
                if (this.$resendConfirmBtn.hasClass("disabled")) return;

                this.$resendConfirmBtn.addClass("disabled").prepend('<div class="loader" />');
                try {
                    await Factory.getInstance<ProfileDao>(ProfileDao).sendEmailConfirmation();
                    Popup.show(trans.profile.confirmationSent);
                    this.$resendConfirmBtn.hide().removeClass("disabled").find("> .loader").remove();
                } catch (ex) {
                    logError(ex);
                    Popup.show(trans.profile.failedResend);
                    this.$resendConfirmBtn.removeClass("disabled").find("> .loader").remove();
                }
            });

        row = $('<div class="formrow" />').appendTo(this.$profileForm);
        this.phoneInput = new PhoneNumberInput(false).appendTo(row);

        row = $('<div class="formrow" />').appendTo(this.$profileForm);
        row.append(
            '<div class="formcontrol"><label id="ziplabel">' +
                trans.signup.zip +
                '</label><input type="text" name="zip" autocomplete="off" value="" /></div>'
        );

        row = $('<div class="formrow" />').appendTo(this.$profileForm);
        this.birthdayInput = new DateInput(trans.covidSignupPopover.birthday, false, {
            startView: 2,
            defaultViewDate: new Date(new Date().getTime() - 86400000 * 30 * 365), // set default to 20 years ago,
            endDate: "today",
        }).appendTo(row);

        const currentPwRow = $('<div class="formrow currentpw" />').appendTo(this.$profileForm);
        this.currentPwInput = new PasswordInput(false, false, trans.profile.currentPassword).appendTo(currentPwRow);

        const newPwRow = $('<div class="formrow newpw" />').appendTo(this.$profileForm);
        this.newPwInput = new PasswordInput(true, false, trans.profile.newPassword).appendTo(newPwRow);

        this.$changePwBtn = $('<a href="#" class="changepw">' + trans.profile.changePassword + "</a>")
            .appendTo(this.$profileForm)
            .click((e) => {
                e.preventDefault();

                if (currentPwRow.is(":visible") || newPwRow.is(":visible")) this.setPasswordResetVisible(false);
                else this.setPasswordResetVisible(true);
            });

        this.$saveBtn = Ripple.bind(
            $('<button type="submit">' + trans.profile.updateBtn + "</button>")
                .appendTo(this.$profileForm)
                .click((e) => {
                    e.preventDefault();
                    if (this.$saveBtn.hasClass("disabled")) {
                        e.stopPropagation();
                        return;
                    }

                    this.$profileForm.submit();
                })
        );

        FloatLabel.bind(this.$profileForm);
    }

    protected onBecomeVisible(data?: IProfileControllerTransitionData) {
        if (Helper.hasService(ServiceType.Covid)) {
            this.useCertificateVerification = Helper.getCovidServiceConfigValue<boolean>("verifyCovidCert") === true;
            this.useContactTracing = Helper.getCovidServiceConfigValue<boolean>("showContactTracing") === true;
        } else {
            this.useCertificateVerification = false;
            this.useContactTracing = false;
            this.isPhoneNumberRequired = data?.phoneNumberRequired;
        }

        //update field labels
        this.$profileForm
            .find("#ziplabel")
            .html(trans.signup.zip + (this.useContactTracing ? "" : " (" + trans.general.optional + ")"));
        this.phoneInput.setLabel(
            trans.covidSignupPopover.phone + (this.isPhoneNumberRequired ?? this.useContactTracing ? "" : " (" + trans.general.optional + ")")
        );
        this.birthdayInput.setLabel(
            trans.covidSignupPopover.birthday + (this.useCertificateVerification ? "" : " (" + trans.general.optional + ")")
        );

        this.setPasswordResetVisible(false);
        this.setFormDataByProfile(TastierClient.identityContext.getProfile()!);
        TastierClient.identityContext.onIdentityChange.add(this.onIdentityChangeHandler, this);
        // Identity.onIdentityChanged.on(this.onIdentityChangeHandler.bind(this));
    }

    protected onBecomeInvisible() {
        TastierClient.identityContext.onIdentityChange.remove(this.onIdentityChangeHandler, this);
        // Identity.onIdentityChanged.off(this.onIdentityChangeHandler);
    }

    private setFormDataByProfile(profile: IProfile) {
        if (profile === undefined) return;

        this.avatarInput.setByProfile(TastierClient.identityContext.getProfile()!);
        this.emailInput.setValue(profile.email!).setVerified(profile.hasConfirmedMail);
        if (profile.email === undefined || profile.hasConfirmedMail) this.$resendConfirmBtn.hide();
        else this.$resendConfirmBtn.show();

        this.phoneInput.setValue(profile.phoneNumber);
        this.$profileForm.find('input[name="firstname"]').val(profile.firstName === undefined ? "" : profile.firstName);
        this.$profileForm.find('input[name="lastname"]').val(profile.lastName === undefined ? "" : profile.lastName);
        this.$profileForm.find('input[name="zip"]').val(profile.zip === undefined ? "" : profile.zip);
        this.birthdayInput.setValue(profile.birthday);

        FloatLabel.check(this.$profileForm);
    }

    private async updateProfile() {
        const firstNameField = this.$profileForm.find('input[name="firstname"]');
        const lastNameField = this.$profileForm.find('input[name="lastname"]');
        const zipField = this.$profileForm.find('input[name="zip"]');
        const isPasswordReset = this.$profileForm.find(".formrow.currentpw").is(":visible");

        const data: IUpdateProfileCmd = {
            firstName: firstNameField.val().length === 0 ? undefined : firstNameField.val(),
            lastName: lastNameField.val().length === 0 ? undefined : lastNameField.val(),
            phoneCountryCode: this.phoneInput.getCountryCode(),
            phoneNumber: this.phoneInput.getNumber(),
            zip: zipField.val().length === 0 ? undefined : zipField.val(),
            birthday: this.birthdayInput.getValue(),
            email: this.emailInput.getValue(),
            avatar: this.avatarInput.getPendingUpload(),
            newPassword: isPasswordReset ? this.newPwInput.getValue() : undefined,
            existingPassword: isPasswordReset ? this.currentPwInput.getValue() : undefined,
        };

        let hasError = false;
        this.$profileForm.find(".has-error").removeClass("has-error");

        if (
            data.firstName === undefined ||
            data.firstName.length < config.minNameLength ||
            data.firstName.length > config.maxNameLength
        ) {
            firstNameField.parent().addClass("has-error");
            hasError = true;
        }

        if (
            data.lastName === undefined ||
            data.lastName.length < config.minNameLength ||
            data.lastName.length > config.maxNameLength
        ) {
            lastNameField.parent().addClass("has-error");
            hasError = true;
        }

        if (data.email === undefined || !this.emailInput.isValid()) {
            this.emailInput.setError(true);
            hasError = true;
        } else this.emailInput.setError(false);

        if (data.phoneNumber !== undefined && !this.phoneInput.isValid()) {
            this.phoneInput.setError(true);
            hasError = true;
        } else this.phoneInput.setError(false);

        if (data.zip !== undefined && !Validation.isZip(data.zip)) {
            zipField.parent().addClass("has-error");
            hasError = true;
        }

        if (this.useContactTracing && data.zip === undefined) {
            zipField.parent().addClass("has-error");
            hasError = true;
        }

        if ((this.isPhoneNumberRequired ?? this.useContactTracing) && data.phoneNumber === undefined) {
            this.phoneInput.setError(true);
            hasError = true;
        }

        if (this.useCertificateVerification && data.birthday === undefined) {
            this.birthdayInput.setError(true);
            hasError = true;
        }

        this.currentPwInput.setError(false);
        this.newPwInput.setError(false);
        if (isPasswordReset) {
            if (!this.currentPwInput.isValid()) {
                hasError = true;
                this.currentPwInput.setError(true);
            }

            if (!this.newPwInput.isValid()) {
                hasError = true;
                this.newPwInput.setError(true);
            }

            if (this.currentPwInput.getValue() === this.newPwInput.getValue()) {
                hasError = true;
                this.currentPwInput.setError(true);
                this.newPwInput.setError(true);
            }
        }

        if (hasError) return;

        this.$saveBtn.prop("disabled", true).addClass("loading");
        try {
            const e = await Factory.getInstance<ProfileDao>(ProfileDao).updateProfile(data);

            if (data.avatar !== undefined && e.avatar !== undefined) {
                e.avatar += "?c=" + new Date().getTime();
            }

            TastierClient.identityContext.aggregateIdentity(e);
            this.setFormDataByProfile(e);

            if (isPasswordReset) {
                this.setPasswordResetVisible(false);
            }

            Popup.show(trans.profile.profileSaved);
        } catch (ex) {
            logError(ex);
            if (isPasswordReset) {
                this.setPasswordResetVisible(false);
            }

            Popup.show(trans.profile.failedSave);
        } finally {
            this.$saveBtn.prop("disabled", false).removeClass("loading");
        }
    }

    private setPasswordResetVisible(isVisible: boolean) {
        const newPwRow = this.$profileForm.find(".formrow.newpw");
        const currentPwRow = this.$profileForm.find(".formrow.currentpw");

        if (isVisible) {
            currentPwRow.stop(true, false).slideDown("fast", "easeOutExpo");
            newPwRow.stop(true, false).slideDown("fast", "easeOutExpo");
            this.$changePwBtn.html(trans.general.cancel);
        } else {
            this.currentPwInput.setValue("");
            this.newPwInput.setValue("");
            currentPwRow.stop(true, false).slideUp("fast", "easeOutExpo");
            newPwRow.stop(true, false).slideUp("fast", "easeOutExpo");
            this.$changePwBtn.html(trans.profile.changePassword);
        }
    }

    private onIdentityChangeHandler() {
        this.setFormDataByProfile(TastierClient.identityContext.getProfile()!);
    }
}
